/**
 * PaymentCompletionCard Component
 *
 * Displays payment completion information and next steps.
 * Ported from IPFiling/Payment (wizard removal) with multi-currency display support.
 * Replaces the payment form when payment has been completed (or refunded).
 */
import React from 'react';
import { CheckCircle, Receipt, RotateCcw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import PaymentStatusBadge from './PaymentStatusBadge';

const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW'];

const PaymentCompletionCard = ({
  application,
  paymentAmount,
  paymentCurrency = 'USD',
  paymentDate,
  isRefunded = false,
  isPartiallyRefunded = false,
  onViewReceipt
}) => {
  const formatCurrency = (minorUnits) => {
    if (!minorUnits) return 'N/A';
    const code = (paymentCurrency || 'USD').toUpperCase();
    const divisor = ZERO_DECIMAL_CURRENCIES.includes(code) ? 1 : 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: code,
      }).format(minorUnits / divisor);
    } catch (e) {
      return `${(minorUnits / divisor).toFixed(2)} ${code}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine card styling and content based on refund status
  const getCardInfo = () => {
    if (isRefunded) {
      return {
        cardClass: "border-orange-200 bg-orange-50",
        iconBgClass: "bg-orange-100",
        iconClass: "text-orange-600",
        titleClass: "text-orange-900",
        textClass: "text-orange-700",
        icon: RotateCcw,
        title: "Refund Issued",
        subtitle: "Your payment has been refunded"
      };
    }

    if (isPartiallyRefunded) {
      return {
        cardClass: "border-orange-200 bg-orange-50",
        iconBgClass: "bg-orange-100",
        iconClass: "text-orange-600",
        titleClass: "text-orange-900",
        textClass: "text-orange-700",
        icon: RotateCcw,
        title: "Partial Refund Issued",
        subtitle: "A partial refund has been processed"
      };
    }

    // Default: Payment completed
    return {
      cardClass: "border-green-200 bg-green-50",
      iconBgClass: "bg-green-100",
      iconClass: "text-green-600",
      titleClass: "text-green-900",
      textClass: "text-green-700",
      icon: CheckCircle,
      title: "Payment Completed Successfully",
      subtitle: "Your trademark application payment has been processed"
    };
  };

  const cardInfo = getCardInfo();
  const CardIcon = cardInfo.icon;

  return (
    <Card className={cardInfo.cardClass}>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${cardInfo.iconBgClass} rounded-full`}>
              <CardIcon className={`h-6 w-6 ${cardInfo.iconClass}`} />
            </div>
            <div>
              <CardTitle className={cardInfo.titleClass}>{cardInfo.title}</CardTitle>
              <Text className={`${cardInfo.textClass} mt-1`}>
                {cardInfo.subtitle}
              </Text>
            </div>
          </div>
          <PaymentStatusBadge
            paymentStatus={application?.paymentStatus}
            status={application?.status}
            amount={paymentAmount}
            currency={paymentCurrency}
            paymentDate={paymentDate}
            refundAmount={application?.refundAmount}
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Payment Details */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <H3 className="text-gray-900 mb-3 flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            {isRefunded || isPartiallyRefunded ? 'Payment & Refund Details' : 'Payment Details'}
          </H3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Text className="text-sm font-medium text-gray-700">Original Amount</Text>
              <Text className="text-lg font-semibold text-gray-900">
                {formatCurrency(paymentAmount)}
              </Text>
            </div>
            {(isRefunded || isPartiallyRefunded) && application?.refundAmount && (
              <div>
                <Text className="text-sm font-medium text-gray-700">Refund Amount</Text>
                <Text className="text-lg font-semibold text-orange-600">
                  {formatCurrency(application.refundAmount)}
                </Text>
              </div>
            )}
            <div>
              <Text className="text-sm font-medium text-gray-700">
                {isRefunded || isPartiallyRefunded ? 'Transaction Date' : 'Payment Date'}
              </Text>
              <Text className="text-lg font-semibold text-gray-900">
                {formatDate(paymentDate)}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium text-gray-700">Application ID</Text>
              <Text className="text-sm text-gray-600 font-mono break-all">
                {application?.id || 'N/A'}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium text-gray-700">Status</Text>
              <Text className={`text-sm font-medium ${
                isRefunded || isPartiallyRefunded
                  ? 'text-orange-600'
                  : 'text-green-600'
              }`}>
                {isRefunded
                  ? 'Payment Refunded'
                  : isPartiallyRefunded
                    ? 'Partially Refunded'
                    : 'Processing Application'
                }
              </Text>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {onViewReceipt && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onViewReceipt}
              variant="outline"
              className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-100"
            >
              <Receipt className="h-4 w-4" />
              View Receipt
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentCompletionCard;
